import { useEffect, useId, useRef } from 'react'
import type { AndroidUpdatePrompt } from '../services/android-app-update'

type Props = {
  prompt: AndroidUpdatePrompt | null
  busy: boolean
  onUpdate: () => void
  onDismiss: () => void
}

function DownloadIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 3v12" strokeLinecap="round" />
      <path d="M7 11l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 21h14" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Soft (dismissible) Android update dialog. Soft by default — force mode can
 * be added later by hiding Not now / blocking dismiss.
 *
 * z-[70] sits above the soft-lock sheet (z-50) so testers can update even if
 * the lock is showing. It would also cover the call UI (z-[60]/[66]), so the
 * gate keeps it off screen while a call is ringing or live.
 */
export function AndroidUpdateModal({ prompt, busy, onUpdate, onDismiss }: Props) {
  const titleId = useId()
  const descId = useId()
  const primaryRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!prompt) return
    primaryRef.current?.focus()
  }, [prompt?.availableVersionCode, prompt?.readyToInstall])

  useEffect(() => {
    if (!prompt) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault()
        onDismiss()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [busy, onDismiss, prompt])

  if (!prompt) return null

  const ready = prompt.readyToInstall
  const title = ready ? 'Update ready' : 'Update available'
  const description = ready
    ? 'A new version has finished downloading. Restart the app to finish installing.'
    : 'A newer version of Ascend Messenger is available on Google Play. Update now to get the latest fixes and features.'
  const primaryLabel = busy ? (ready ? 'Restarting…' : 'Updating…') : ready ? 'Restart to finish' : 'Update now'

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm native-modal-top"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="animate-fade-slide-in w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex flex-col items-center gap-3 px-6 pb-6 pt-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
            <DownloadIcon />
          </div>
          <h2 id={titleId} className="text-lg font-bold text-slate-900">
            {title}
          </h2>
          <p id={descId} className="text-sm text-slate-600">
            {description}
          </p>

          <div className="mt-3 flex w-full flex-col gap-2">
            <button
              ref={primaryRef}
              type="button"
              onClick={onUpdate}
              disabled={busy}
              className="w-full rounded-xl bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              {primaryLabel}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="w-full rounded-xl px-4 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-slate-100 disabled:opacity-60"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
